import { useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-medicine-bottle-line',
    title: 'Gôndolas e expositores que aumentam o ticket médio da farmácia',
    description: 'Produto bem exposto é produto vendido. Gôndolas organizadas, iluminação adequada e expositores estratégicos fazem o cliente comprar mais do que planejava — e voltar mais vezes.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Materiais higienizáveis e conformes com a ANVISA',
    description: 'MDF revestido em laminado HPL resistente a álcool 70%, superfícies lisas sem poros e acabamentos que resistem a desinfetantes. Sua farmácia em conformidade com as normas sanitárias.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito do layout completo',
    description: 'Área de vendas, balcão de atendimento, dispensário e recepção projetados em 3D. Você aprova o planograma antes de fabricar — e garante que cada produto tenha o melhor posicionamento.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Montagem sem fechar a farmácia',
    description: 'Nossa equipe instala setor por setor, nos horários de menor movimento. Sua farmácia não perde um dia de faturamento durante a reforma.',
  },
];

const blocks = [
  {
    title: 'Gôndolas e Expositores que Transformam Visita em Venda',
    text: [
      'Uma farmácia bem organizada vende mais. O cliente que entra para comprar um remédio e encontra um expositor bem montado de vitaminas, dermocosméticos ou produtos de higiene pessoal compra mais do que planejava. O planograma certo — produto certo, na altura certa, com iluminação adequada — pode aumentar o ticket médio em 30% a 50%.',
      'Nossas gôndolas para farmácias incluem estrutura em aço com pintura eletrostática anticorrosiva, prateleiras em MDF revestido em laminado HPL branco resistente a produtos de limpeza, iluminação LED embutida nas prateleiras superiores que destaca os produtos, porta-preços e porta-etiquetas integrados, e rodapé em MDF que fecha o visual e facilita a limpeza do piso. Disponíveis em módulos de 0,60m, 0,90m e 1,20m de largura para adaptar a qualquer layout.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20pharmacy%20interior%20with%20organized%20gondola%20shelves%2C%20products%20neatly%20displayed%2C%20LED%20lighting%2C%20clean%20white%20design%2C%20professional%20retail%20pharmacy%20layout%2C%20warm%20lighting%2C%20high-end%20drugstore%20interior%2C%20professional%20photography&width=800&height=500&seq=farm_v1_1&orientation=landscape',
    reverse: false,
  },
  {
    title: 'Balcão de Atendimento e Dispensário que Transmitem Confiança',
    text: [
      'O balcão de atendimento é onde o farmacêutico orienta o cliente, verifica a receita e entrega o medicamento. Ele precisa transmitir profissionalismo e organização — porque o cliente está confiando sua saúde ao profissional que está do outro lado. Um balcão bem projetado reforça essa confiança.',
      'Projetamos balcões de atendimento para farmácias com tampo em granito ou laminado HPL resistente a produtos químicos, espaço interno organizado para computador, impressora de receitas e documentos, gavetas com corrediças telescópicas para organizar medicamentos de uso frequente, frente em MDF com acabamento que pode receber o logo da farmácia, e dispensário com armários fechados para medicamentos controlados e prateleiras abertas para os de venda livre.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20pharmacy%20counter%20with%20professional%20pharmacist%20desk%2C%20organized%20dispensary%20shelves%20behind%2C%20clean%20white%20design%2C%20warm%20lighting%2C%20professional%20healthcare%20retail%20interior%2C%20trust%20and%20competence%20design&width=800&height=500&seq=farm_v1_2&orientation=landscape',
    reverse: true,
  },
  {
    title: 'Área de Dermocosméticos, Perfumaria e Recepção Completas',
    text: [
      'As categorias de maior margem da farmácia — dermocosméticos, perfumaria, suplementos e produtos de higiene premium — precisam de expositores especiais que valorizem os produtos e justifiquem o preço. Uma ilha de dermocosméticos bem iluminada, com espelho e testadores organizados, pode gerar mais receita por metro quadrado do que qualquer outra área da farmácia.',
      'Para a área de dermocosméticos, projetamos ilhas e expositores com iluminação LED de alta reprodução de cor (IRC 90+) que mostra as cores reais dos produtos, espelhos com moldura em alumínio, prateleiras em vidro temperado que valorizam os produtos, e espaço para testadores organizados. Para a recepção e área de espera, balcão com tampo em granito, cadeiras confortáveis e painel de TV para informações de saúde.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20pharmacy%20cosmetics%20and%20dermocosmetics%20display%20area%20with%20LED%20lighting%2C%20glass%20shelves%2C%20organized%20products%2C%20elegant%20retail%20design%2C%20premium%20drugstore%20interior%2C%20warm%20lighting%2C%20professional%20photography&width=800&height=500&seq=farm_v1_3&orientation=landscape',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: 'https://readdy.ai/api/search-image?query=Modern%20pharmacy%20interior%20with%20organized%20gondola%20shelves%2C%20LED%20lighting%2C%20clean%20white%20design%2C%20professional%20retail%20layout&width=600&height=400&seq=farm_g1&orientation=landscape',
    alt: 'Gôndolas organizadas de farmácia com iluminação LED',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Pharmacy%20dispensary%20counter%20with%20organized%20shelves%2C%20professional%20design%2C%20clean%20white%20interior%2C%20warm%20lighting&width=600&height=400&seq=farm_g2&orientation=landscape',
    alt: 'Balcão de atendimento e dispensário de farmácia',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Pharmacy%20cosmetics%20display%20with%20LED%20lighting%2C%20glass%20shelves%2C%20organized%20products%2C%20elegant%20retail%20design&width=600&height=400&seq=farm_g3&orientation=landscape',
    alt: 'Expositor de dermocosméticos com iluminação especial',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Modern%20pharmacy%20reception%20area%20with%20counter%2C%20waiting%20chairs%2C%20clean%20professional%20design%2C%20warm%20lighting&width=600&height=400&seq=farm_g4&orientation=landscape',
    alt: 'Recepção e área de espera de farmácia',
  },
];

const WA_LINK = 'https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Farm%C3%A1cias%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!';

export default function FarmaciasPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Farmácia Caruaru PE | Gôndolas e Balcões – Home Office Premium',
    description: 'Mobiliário para farmácias e drogarias em Caruaru PE. Gôndolas, balcões de atendimento, dispensário e expositores. Materiais higienizáveis ANVISA, projeto 3D gratuito e montagem sem fechar a farmácia. Atendemos farmácias no Agreste e Sertão de Pernambuco.',
    keywords: 'mobiliário farmácia Recife, gôndola farmácia Petrolina, balcão farmácia Garanhuns, dispensário farmácia Caruaru, móveis drogaria Agreste PE, gôndola drogaria Pernambuco, expositor farmácia Nordeste, balcão atendimento farmácia Recife, mobiliário farmácia Caruaru',
    path: '/farmacias',
  });

  useSchemaLD({
    id: 'schema-farmacias',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Farmácias e Drogarias | Caruaru PE',
      description: 'Gôndolas, balcões de atendimento, dispensário e expositores para farmácias. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/farmacias`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Farmácias e Drogarias',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20professional%20pharmacy%20interior%20with%20organized%20shelves%2C%20clean%20white%20design%2C%20LED%20lighting%2C%20professional%20retail%20layout%2C%20warm%20atmosphere%2C%20high-end%20drugstore%2C%20wide%20angle%20photography&width=1400&height=700&seq=farm_hero&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Farmácias · Drogarias · Manipulação
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Gôndolas e expositores que aumentam o ticket médio da sua farmácia.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Produto bem exposto é produto vendido. Layout estratégico, iluminação adequada e materiais higienizáveis conformes com a ANVISA — porque farmácia é saúde e negócio ao mesmo tempo.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Montagem sem fechar a farmácia · Suporte local em Caruaru e Agreste
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que farmácias de Caruaru escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes de Farmácia</span> que Vendem Mais e Transmitem Confiança
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja o layout completo da sua farmácia em 3D antes de investir.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Gôndolas, balcão, dispensário e expositores projetados em 3D com planograma estratégico. Você aprova cada detalhe antes de fabricarmos. Montagem sem fechar a farmácia.
          </p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Ambientes de <span className="text-[#E8610A]">Farmácia Premium</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href={WA_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
